import { useMemo, useRef } from 'react'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { useReactToPrint } from 'react-to-print'
import { Grid, NumberFormatter, Badge, Stack } from '@mantine/core'

import { tableDefaultAlertBannerProps, tableDefaultProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { showErrorNotification } from '@shared/helpers/notifications/notifications'
import { WoolContract } from '@loom-api-types'
import { PrintOnly } from '@shared/components/print-only/print-only'
import { transposeDataToContractDetails } from '@shared/components/wool-data/wool-contracts-table/contract-details-helpers'
import { ContractDetailsSubtable } from '@shared/components/wool-data/wool-contracts-table/contract-details-subtable'
import { ContractedLotsSubtable } from '@shared/components/wool-data/wool-contracts-table/contracted-lots-subtable'
import { WoolContractsPrint } from '@shared/components/wool-data/wool-contracts-table/wool-contracts-print'

interface WoolContractsTableProps {
  data: WoolContract[]
  renderKey?: string
  sumOfBalesContracted?: number
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
  printButtonRef?: React.RefObject<HTMLButtonElement>
  season?: string
  brand?: string
}

// Check if the contract is a REDA contract
// This is used to determine if the REDA type should be included in the table and in the printout
export const isRedaContract = (contract: WoolContract): boolean => {
  return contract.contractName ? contract.contractName.includes('REDA') : false
}

export const WoolContractsTable = ({
  data = [],
  renderKey = '',
  sumOfBalesContracted = 0,
  noDataMessage = 'No records to display',
  isLoading,
  isError,
  printButtonRef,
  season = '',
  brand = '',
}: WoolContractsTableProps) => {
  const columns = useMemo<MRT_ColumnDef<WoolContract>[]>(
    () => [
      {
        id: 'contractName',
        accessorKey: 'contractName',
        header: 'Contract Name',
        Cell: ({ row }) => (
          <Stack gap={0}>
            {row.original.contractName}
            {!row.original.contractIsSigned && (
              <Badge color='red.2' c='black' fw={600} aria-label='Status Unsigned'>
                Unsigned
              </Badge>
            )}
          </Stack>
        ),
        Footer: () => <>Totals</>,
      },
      {
        id: 'purchaseAccountRecordNumber',
        accessorKey: 'purchaseAccountRecordNumber',
        header: 'Contract Reference',
        Header: () => (
          <>
            Contract
            <br />
            Reference
          </>
        ),
      },
      {
        id: 'balesContracted',
        accessorKey: 'balesContracted',
        header: 'Bales Contracted',
        Header: () => (
          <>
            Bales
            <br />
            Contracted
          </>
        ),
        Footer: () => <NumberFormatter value={sumOfBalesContracted} thousandSeparator />,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
      },
      {
        id: 'targetMicron',
        accessorKey: 'targetMicron',
        header: 'Target Micron',
        Header: () => (
          <>
            Target
            <br />
            Micron
          </>
        ),
        Cell: ({ cell }) => <NumberFormatter value={cell.getValue<number>()} decimalScale={2} />,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
      },
      {
        id: 'contractDeliveryDate',
        accessorKey: 'contractDeliveryDate',
        header: 'Delivery Date',
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
      },
      {
        id: 'averagePriceCLN',
        accessorKey: 'averagePriceCLN',
        header: 'Average Price (Clean C/KG)',
        Cell: ({ cell }) => (
          <NumberFormatter suffix='c' value={cell.getValue<number>()} thousandSeparator />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
      },
    ],
    [sumOfBalesContracted]
  )

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    state: {
      isLoading,
      showAlertBanner: isError,
    },
    initialState: {
      ...tableDefaultProps.initialState,
      pagination: undefined,
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      withColumnBorders: false,
      'aria-label': 'Wool Contracts',
    },
    renderDetailPanel: ({ row }) => {
      const additionalData = transposeDataToContractDetails(row.original)
      return (
        <Grid>
          <Grid.Col span={{ base: 12, lg: 3 }}>
            <ContractDetailsSubtable data={additionalData} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 9 }}>
            <ContractedLotsSubtable
              data={row.original.contractedLots}
              totalBalesAccepted={row.original.totalBalesAccepted}
              includeRedaType={isRedaContract(row.original)}
            />
          </Grid.Col>
        </Grid>
      )
    },
    enablePagination: false,
  })

  // Print table functionality
  // -------------------------
  // The following logic is used to print the wool-contracts-print component, triggered by clicking a button
  // provided to the table component.
  //
  // The print function is provided by the react-to-print library, which requires a reference to the element
  // to be printed. This is achieved by wrapping the table in a div and passing a reference to the div to the
  // print function.

  // Store the table reference to pass to the print function
  const printComponentRef = useRef<HTMLDivElement>(null)

  // Printing function as defined by https://github.com/MatthewHerbst/react-to-print
  const handlePrintButtonClick = useReactToPrint({
    contentRef: printComponentRef,
  })

  const printContracts = () => {
    try {
      handlePrintButtonClick()
    } catch (error) {
      showErrorNotification('Printing failed. Please try again.')
    }
  }

  // Attach the print function to the print button
  if (printButtonRef?.current) {
    printButtonRef.current.onclick = printContracts
  }

  return (
    <>
      <div data-testid='wool-contracts-table'>
        <MantineReactTable table={table} key={renderKey} />
      </div>
      <PrintOnly ref={printComponentRef}>
        <WoolContractsPrint
          data={data}
          sumOfBalesContracted={sumOfBalesContracted}
          season={season}
          brand={brand}
        />
      </PrintOnly>
    </>
  )
}
